import {productPricesForMarket} from '@/components/products/productDisplay'

/**
 * Pure campaign helpers (JO2-247), safe to import from client components:
 * no Sanity client or server-only imports here. `lib/campaigns.ts` re-exports
 * them next to the fetchers.
 */

/** True when `now` is inside the campaign's duration. Open ends count as open. */
export const isCampaignActive = (
  duration?: {startAt?: string; endAt?: string} | null,
  now?: string,
): boolean => {
  const at = new Date(now || Date.now()).getTime()
  const start = duration?.startAt ? new Date(duration.startAt).getTime() : Number.NEGATIVE_INFINITY
  const end = duration?.endAt ? new Date(duration.endAt).getTime() : Number.POSITIVE_INFINITY
  return start <= at && end >= at
}

export type CampaignProducts = {
  duration?: {startAt?: string; endAt?: string} | null
  productIds?: Array<string | null> | null
}

/** JO2-247: the products listed on the campaigns that are active at `now`. */
export const activeCampaignProductIds = (
  campaigns: ReadonlyArray<CampaignProducts>,
  now?: string,
): Set<string> =>
  new Set(
    campaigns
      .filter((campaign) => isCampaignActive(campaign.duration, now))
      .flatMap((campaign) => campaign.productIds || [])
      .filter((id): id is string => Boolean(id)),
  )

/**
 * JO2-247: flag the products listed on an active campaign, so ProductCard can
 * show the campaign badge without knowing about campaigns. Returns new objects.
 */
export const markActiveCampaignProducts = <
  T extends {_id?: string; objectID?: string; onActiveCampaign?: boolean},
>(
  products: ReadonlyArray<T>,
  ids: ReadonlySet<string>,
): T[] =>
  products.map((product) => {
    // Catalog products come from the search index: the id is `objectID`, and
    // there is no `_id` (OpenSearch reserves it). Sanity documents have `_id`.
    const id = product._id || product.objectID
    return id && ids.has(id) ? {...product, onActiveCampaign: true} : product
  })

/**
 * JO2-247 (agreed with Jøtul 2026-10-26): a product is on campaign in a market
 * when it is listed on an active campaign for the site (`onActiveCampaign`,
 * set by markActiveCampaignProducts), or when a variant has a campaign price
 * in that market inside its window. A hidden price is not a campaign signal.
 *
 * The one rule for the campaign badge (ProductCard) and the "On Campaign"
 * listing filter (/api/search), so the two cannot disagree.
 */
export const productOnCampaign = (
  product: Parameters<typeof productPricesForMarket>[0] & {onActiveCampaign?: boolean},
  marketKey: string | undefined,
  hideProductPrices = false,
): boolean =>
  Boolean(product.onActiveCampaign) ||
  (!hideProductPrices &&
    productPricesForMarket(product, marketKey).some(
      (price) => 'campaignAmount' in price && price.campaignAmount !== undefined,
    ))
